import { useCallback, useState } from 'react';
import type { NavGroupId } from './nav-items';

// Collapsible rail groups (design D:7170 `railCollapsed`, persisted in
// localStorage). A per-viewer convenience, so browser storage is the right home;
// every read/write is guarded because storage can be blocked. The set holds the
// COLLAPSED group ids; `main` (Overview) is never collapsible.

export const RAIL_GROUPS_STORAGE_KEY = 'bd.admin.rail-groups';

const COLLAPSIBLE: readonly NavGroupId[] = ['ops', 'people', 'revenue', 'platform', 'system'];

export function readCollapsed(): Set<NavGroupId> {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(RAIL_GROUPS_STORAGE_KEY) : null;
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((g): g is NavGroupId => COLLAPSIBLE.includes(g as NavGroupId)));
  } catch {
    return new Set();
  }
}

function write(next: Set<NavGroupId>) {
  try {
    localStorage.setItem(RAIL_GROUPS_STORAGE_KEY, JSON.stringify([...next]));
  } catch {
    // Storage blocked — the choice still holds for this session.
  }
}

export interface RailGroupsState {
  collapsed: Set<NavGroupId>;
  toggle: (group: NavGroupId) => void;
  /** Design double-click: open this group, collapse every other collapsible one. */
  focus: (group: NavGroupId) => void;
  /** Collapse every group in `groups`, or expand them all if they already are. */
  toggleAll: (groups: readonly NavGroupId[]) => void;
}

export function useRailGroups(): RailGroupsState {
  const [collapsed, setCollapsed] = useState<Set<NavGroupId>>(readCollapsed);

  const update = useCallback((fn: (prev: Set<NavGroupId>) => Set<NavGroupId>) => {
    setCollapsed((prev) => {
      const next = fn(prev);
      write(next);
      return next;
    });
  }, []);

  const toggle = useCallback(
    (group: NavGroupId) => {
      if (!COLLAPSIBLE.includes(group)) return;
      update((prev) => {
        const next = new Set(prev);
        if (next.has(group)) next.delete(group);
        else next.add(group);
        return next;
      });
    },
    [update],
  );

  const focus = useCallback(
    (group: NavGroupId) => update(() => new Set(COLLAPSIBLE.filter((g) => g !== group))),
    [update],
  );

  const toggleAll = useCallback(
    (groups: readonly NavGroupId[]) =>
      update((prev) => {
        const targets = groups.filter((g) => COLLAPSIBLE.includes(g));
        const allCollapsed = targets.length > 0 && targets.every((g) => prev.has(g));
        return allCollapsed ? new Set() : new Set(targets);
      }),
    [update],
  );

  return { collapsed, toggle, focus, toggleAll };
}
